import { automationUrgentBonus } from "./bonuses";
import { isContractUnlocked } from "./progression";
import {
  MAX_OFFERS,
  URGENT_BONUS_PCT,
  URGENT_COOLDOWN_MAX_MS,
  URGENT_COOLDOWN_MIN_MS,
  URGENT_DURATIONS_MS,
} from "./offerConstants";
import type { Contract, OfferBoardState, Player, UrgentOffer } from "../types";

export interface OffersContext {
  contracts: readonly Contract[];
  player: Player;
  now: number;
  rng: () => number;
}

function byId(contracts: readonly Contract[], id: string): Contract | undefined {
  return contracts.find((c) => c.id === id);
}

function eligibleContracts(ctx: OffersContext): Contract[] {
  return ctx.contracts
    .filter((c) => isContractUnlocked(c, ctx.player) && !ctx.player.completedContracts.includes(c.id))
    .sort(
      (a, b) =>
        a.requiredReputation - b.requiredReputation ||
        a.difficulty - b.difficulty ||
        a.id.localeCompare(b.id),
    );
}

export function lockedContracts(ctx: OffersContext): Contract[] {
  return ctx.contracts
    .filter((c) => !isContractUnlocked(c, ctx.player) && !ctx.player.completedContracts.includes(c.id))
    .sort((a, b) => a.requiredReputation - b.requiredReputation || a.id.localeCompare(b.id));
}

function pick<T>(items: readonly T[], rng: () => number): T | undefined {
  if (items.length === 0) {
    return undefined;
  }
  const index = Math.min(items.length - 1, Math.floor(rng() * items.length));
  return items[index];
}

function makeUrgent(available: string[], previousId: string | undefined, ctx: OffersContext): UrgentOffer | null {
  const candidates = available.filter((id) => id !== previousId);
  const poolForPick = candidates.length > 0 ? candidates : available;
  const contractId = pick(poolForPick, ctx.rng);
  if (contractId === undefined) {
    return null;
  }
  const duration = pick(URGENT_DURATIONS_MS, ctx.rng) ?? URGENT_DURATIONS_MS[0]!;
  const bonusPct = URGENT_BONUS_PCT + automationUrgentBonus(ctx.player.skills?.automation ?? 0);
  return { contractId, deadlineMs: ctx.now + duration, bonusPct, durationMs: duration };
}

/** A random cooldown length in [URGENT_COOLDOWN_MIN_MS, URGENT_COOLDOWN_MAX_MS]. */
function randomCooldownMs(rng: () => number): number {
  const span = URGENT_COOLDOWN_MAX_MS - URGENT_COOLDOWN_MIN_MS;
  return URGENT_COOLDOWN_MIN_MS + Math.floor(rng() * span);
}

/**
 * Validates and repairs the offer pool: prunes completed/locked offers and
 * refills `available` up to MAX_OFFERS from eligible contracts (deterministic
 * order). The urgent slot follows a cooldown: when an active urgent ends
 * (consumed or expired) a random 3-10 min gap is scheduled before the next one;
 * the first urgent on a fresh board still spawns immediately. Returns a new
 * state; the input is never mutated.
 */
export function refreshOffers(prev: OfferBoardState, ctx: OffersContext): OfferBoardState {
  const eligible = eligibleContracts(ctx);
  const eligibleIds = new Set(eligible.map((c) => c.id));

  const kept = prev.available.filter((id) => eligibleIds.has(id));
  const available = [...kept];
  for (const c of eligible) {
    if (available.length >= MAX_OFFERS) {
      break;
    }
    if (!available.includes(c.id)) {
      available.push(c.id);
    }
  }

  const urgentValid =
    prev.urgent !== null &&
    available.includes(prev.urgent.contractId) &&
    ctx.now < prev.urgent.deadlineMs;

  const lastUrgentId = prev.urgent?.contractId ?? prev.lastUrgentId ?? null;

  if (urgentValid) {
    return { available, urgent: prev.urgent, nextUrgentAtMs: null, lastUrgentId };
  }

  const cooldownUntil = prev.nextUrgentAtMs ?? null;

  if (cooldownUntil !== null && ctx.now < cooldownUntil) {
    return { available, urgent: null, nextUrgentAtMs: cooldownUntil, lastUrgentId };
  }

  if (cooldownUntil === null && prev.urgent !== null) {
    return {
      available,
      urgent: null,
      nextUrgentAtMs: ctx.now + randomCooldownMs(ctx.rng),
      lastUrgentId,
    };
  }

  const urgent = makeUrgent(available, lastUrgentId ?? undefined, ctx);
  return { available, urgent, nextUrgentAtMs: null, lastUrgentId: urgent?.contractId ?? lastUrgentId };
}

export function resolveBoard(
  state: OfferBoardState,
  ctx: OffersContext,
): {
  urgent: { contract: Contract; deadlineMs: number; bonusPct: number; durationMs: number } | null;
  available: Contract[];
  locked: Contract[];
} {
  const urgentContract = state.urgent ? byId(ctx.contracts, state.urgent.contractId) : undefined;
  const urgent =
    state.urgent && urgentContract
      ? {
          contract: urgentContract,
          deadlineMs: state.urgent.deadlineMs,
          bonusPct: state.urgent.bonusPct,
          durationMs: state.urgent.durationMs,
        }
      : null;

  const available = state.available
    .filter((id) => id !== state.urgent?.contractId)
    .map((id) => byId(ctx.contracts, id))
    .filter((c): c is Contract => c !== undefined);

  return { urgent, available, locked: lockedContracts(ctx) };
}

export function isUrgentActive(state: OfferBoardState, contractId: string, now: number): boolean {
  return state.urgent !== null && state.urgent.contractId === contractId && now < state.urgent.deadlineMs;
}
